import React from "react";
import type { OtcPopupProduct } from "data-types";
import "./OTCPopupV3.css";
import X from "../../../icons/X.svg";
import * as translator from "../../../utils/translator";
import { useCartContext } from "../../../customHooks/useCart";
import { useShippingPageContext } from "../../../pages/v2/ShippingV2/providers/ShippingPageProvider";
import { OtcPopupTrackingContext, trackOtcPopupClick } from "../../../utils/otcPopupExperiment";
import OTCPopupProductCard from "./OTCPopupProductCard";
import OTCPopupSearch from "./OTCPopupSearch";
import { useOtcPopupSearch } from "./useOtcPopupSearch";

export type OTCPopupV3Props = {
  products: OtcPopupProduct[];
  language: string;
  country: string;
  hideModal: () => void;
  trackingContext: OtcPopupTrackingContext;
  isCarePlus?: boolean;
};

const SOURCE_POPUP = "otc_popup";
const SOURCE_SEARCH = "otc_popup_search";

const OTCPopupV3: React.FC<OTCPopupV3Props> = ({ products, language, country, hideModal, trackingContext, isCarePlus = false }) => {
  const { getCart } = useCartContext();
  const { handleOTCProductIncrement, handleOTCProductDecrement } = useShippingPageContext();
  const search = useOtcPopupSearch(country, language);
  const cart = getCart();

  const hasOtcInCart = cart.products.some((p) => !p.is_prescription_drug && p.quantity > 0);
  const positionOf = (product: OtcPopupProduct) => products.findIndex((p) => p.id === product.id) + 1;

  const onIncrement = (product: OtcPopupProduct, currentQuantity: number) => {
    trackOtcPopupClick(trackingContext, { clickType: currentQuantity > 0 ? "increase_quantity" : "add_to_cart", product, position: positionOf(product) });
    void handleOTCProductIncrement(product as any, SOURCE_POPUP);
  };
  const onDecrement = (product: OtcPopupProduct) => {
    trackOtcPopupClick(trackingContext, { clickType: "decrease_quantity", product, position: positionOf(product) });
    void handleOTCProductDecrement(product as any);
  };
  const onInfoToggle = (product: OtcPopupProduct, open: boolean) => {
    if (open) trackOtcPopupClick(trackingContext, { clickType: "more_information", product, position: positionOf(product) });
  };
  const onSearchFocus = () => trackOtcPopupClick(trackingContext, { clickType: "search_focus" });
  const onResultClick = (product: OtcPopupProduct, index: number) => {
    trackOtcPopupClick(trackingContext, { clickType: "search_result_click", product, position: index + 1, searchResultCount: search.resultCount });
  };
  const onResultAdd = (product: OtcPopupProduct, index: number) => {
    trackOtcPopupClick(trackingContext, { clickType: "search_add_to_cart", product, position: index + 1, searchResultCount: search.resultCount });
    void handleOTCProductIncrement(product as any, SOURCE_SEARCH);
  };
  const onResultIncrement = (product: OtcPopupProduct, index: number) => {
    trackOtcPopupClick(trackingContext, { clickType: "increase_quantity", product, position: index + 1, searchResultCount: search.resultCount });
    void handleOTCProductIncrement(product as any, SOURCE_SEARCH);
  };
  const onResultDecrement = (product: OtcPopupProduct, index: number) => {
    trackOtcPopupClick(trackingContext, { clickType: "decrease_quantity", product, position: index + 1, searchResultCount: search.resultCount });
    void handleOTCProductDecrement(product as any);
  };
  const onClear = () => search.clear();
  // A click anywhere in the popup outside the search box closes the search, like its X
  const onPopupClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (search.query && !(e.target as Element).closest(".otc-popup-v3-search")) onClear();
  };
  const onContinue = () => { trackOtcPopupClick(trackingContext, { clickType: "continue" }); hideModal(); };
  const onClose = () => { trackOtcPopupClick(trackingContext, { clickType: "close_popup" }); hideModal(); };

  return (
    <div className={`otc-popup-v3${isCarePlus ? " otc-popup-v3--care-plus" : ""}`} onClick={onPopupClick}>
      <button type="button" className="otc-popup-v3-close" data-testid="otc-popup-v3-close" onClick={onClose}><img src={X} alt="×" /></button>
      <h1 className="otc-popup-v3-title">{translator.translate(language, "shipping", "OTCPopupV3/title")}</h1>
      <p className="otc-popup-v3-subtitle">{translator.translate(language, "shipping", "OTCPopupV3/subtitle")}</p>
      <OTCPopupSearch
        language={language}
        query={search.query}
        status={search.status}
        results={search.results}
        onQueryChange={search.setQuery}
        onFocus={onSearchFocus}
        onClear={onClear}
        onResultClick={onResultClick}
        onResultAdd={onResultAdd}
        onResultIncrement={onResultIncrement}
        onResultDecrement={onResultDecrement}
      />
      <div className="otc-popup-v3-list">
        {products.map((product) => (
          <OTCPopupProductCard key={product.id} product={product} language={language} isCarePlus={isCarePlus} onIncrement={onIncrement} onDecrement={onDecrement} onInfoToggle={onInfoToggle} />
        ))}
      </div>
      <button type="button" id="otc-popup-v3-continue" className="otc-popup-v3-continue" onClick={onContinue}>
        {translator.translate(language, "shipping", hasOtcInCart ? "OTCPopupV3/continue_with_products" : "OTCPopupV3/continue_without_products")}
      </button>
    </div>
  );
};

export default OTCPopupV3;
